import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { FormError } from '../shared/FormError';
import { Spinner } from '../../ui/Spinner';
import { btnOnClay, inlineHit } from '../../ui/styles';
import { useSubmit } from '../../hooks/useSubmit';
import { demoNewsletterLink, newRequestId, subscribeNewsletter } from '../../utils/api';
import { legalLinks } from '../../data/legal';
import { founder } from '../../data/content';
import { newsletterConsent } from '../../data/consent';
import { ease } from '../../core/format/motion';
import { track } from '../../utils/analytics';

/**
 * Nieuwsbrief bovenaan de footer (op klei): één veld, double opt-in, en een duidelijke
 * bevestiging. Alle tekst in shell (5,6:1 op klei); het accent in blush alleen groot.
 */
export function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const signup = useSubmit(subscribeNewsletter);
  const reduce = useReducedMotion();
  return <div className="grid gap-10 py-16 lg:grid-cols-12 lg:items-end lg:gap-16 lg:py-24" data-id="element-7974" data-mp="3bptazqobp1in">
      <div className="lg:col-span-6" data-id="element-7975" data-mp="3gkh27xapoa0x">
        <h2 id="newsletter-heading" className="type-sub text-shell" data-id="element-7976" data-mp="32rhnvymo0luy">
          De vijf fouten die ik
          <span className="italic text-blush" data-id="element-7977" data-mp="37pj50e0j6b3s"> het vaakst zie.</span>
        </h2>
        <p className="mt-5 max-w-md text-[17px] leading-relaxed text-shell" data-id="element-7978" data-mp="3vfn88k9t48u9">
          Meld je aan en je krijgt ze meteen in je inbox. Plus één keer per maand wat {founder.firstName} in de salon opviel. Geen aanbiedingenspam.
        </p>
      </div>
      {/* De bevestiging heeft zelf role="status"; geen extra live-regio nodig. */}
      <div className="lg:col-span-6" data-id="element-7979" data-mp="3qfr1wedn05bj">
        <AnimatePresence mode="wait" initial={false} data-id="element-7980" data-mp="3qepjtpy7eikb">
          {signup.succeeded ? <motion.p key="done" initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease
        }} role="status" className="flex items-center gap-3 text-[17px] text-shell" data-id="element-7981" data-mp="2a0pmmmqqt70r">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-shell text-clay" data-id="element-7982" data-mp="1m6yz2pjl3q07">
                <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-7983" data-mp="1h843en0iq6yo" />
              </span>
              <span data-id="element-7984" data-mp="1c9arv04skq15">
                Bijna klaar: bevestig je aanmelding via de mail naar {email.trim()}.
                {/* DEMO: met een backend is demoNewsletterLink null en verdwijnt deze knop vanzelf. */}
                {demoNewsletterLink && <Link to={demoNewsletterLink} className="mt-2 block text-[14px] text-shell underline underline-offset-4" data-id="element-7985" data-mp="138cu2zky5er0">
                    Open de bevestigingslink (demo)
                  </Link>}
              </span>
            </motion.p> : <motion.form key="form" exit={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease
        }} aria-labelledby="newsletter-heading" aria-busy={signup.submitting} onSubmit={e => {
          e.preventDefault();
          if (signup.submitting) return;
          void signup.submit({
            requestId: newRequestId(),
            email: email.trim(),
            source: 'footer',
            consentVersion: newsletterConsent.version
          }).then(ok => {
            if (ok) track('nieuwsbrief-aangemeld', {
              bron: 'footer'
            });
          });
        }} data-id="element-7986" data-mp="2socan1qwa6go">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end" data-id="element-7987" data-mp="2r5m1tzcngv17">
                <label htmlFor="footer-email" className="sr-only" data-id="element-7988" data-mp="2v77ebjl9v7av">
                  E-mailadres
                </label>
                <input id="footer-email" type="email" required autoComplete="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jouw@email.nl" aria-invalid={signup.error ? true : undefined} aria-describedby={signup.error ? 'footer-email-error footer-email-hint' : 'footer-email-hint'} className="w-full border-b border-shell/50 bg-transparent px-1 py-3 text-[17px] text-shell placeholder:text-shell/80 transition-colors duration-150 ease-soft focus:border-shell focus:outline-none" data-id="element-7989" data-mp="6gei2fmvhheu" />
                <button type="submit" disabled={signup.submitting} className={`${btnOnClay} group disabled:cursor-wait disabled:opacity-80`} data-id="element-7990" data-mp="mxrexapxcmyc">
                  {signup.submitting ? <Spinner data-id="element-7991" data-mp="1ap55bb4im8go" /> : null}
                  Stuur me de vijf fouten
                  {!signup.submitting && <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7992" data-mp="16ouqra2nq9q7" />}
                </button>
              </div>
              <p id="footer-email-hint" className="mt-4 text-[12px] leading-relaxed text-shell" data-id="element-7993" data-mp="1uaf6zhxkxfe5">
                {newsletterConsent.text} Lees ons{' '}
                <Link to={legalLinks.privacy} className={`${inlineHit} underline underline-offset-2`} data-id="element-7994" data-mp="1la0he6dl1e4u">
                  privacybeleid
                </Link>
                .
              </p>
              <FormError id="footer-email-error" message={signup.error} className="mt-4" data-id="element-7995" data-mp="1bc2nb291kn4b" />
            </motion.form>}
        </AnimatePresence>
      </div>
    </div>;
}